import { Injectable } from "@angular/core";
import { IRiskDetailsResponse } from "@gloria-public/risk/domain/interfaces/knowledge.interface";
import { IRiskFilters, IRiskTemplateResponse } from "@gloria-public/risk/domain/interfaces/risk.interface";
import { ApiService } from "@shared/services/api.service";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class RiskService {
  constructor(private api: ApiService) {}

  /**
   * Returns a paginated list of active risks with content in a given language
   * @param filters Filters to apply (lang, tags, sortByDate, page, pageSize)
   * @returns A paginated list of risks
   */
  public getRisks(filters: IRiskFilters = {}): Observable<IRiskTemplateResponse[]> {
    const params: Record<string, string | number> = {
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 10,
    };
    if (filters.lang) params["lang"] = filters.lang;
    if (filters.tags?.length) params["tags"] = filters.tags.join(",");
    if (filters.sortByDate) params["sortByDate"] = filters.sortByDate;

    return this.api.get("risks", params, false) as Observable<IRiskTemplateResponse[]>;
  }

  /**
   * Returns the total count of active risks based on filters
   * @param filters Filters to apply (tags only — lang/page/pageSize not used for count)
   * @returns Total count of risks
   */
  public getRisksCount(filters: Pick<IRiskFilters, "tags"> = {}): Observable<number> {
    const params: Record<string, string> = {};
    if (filters.tags?.length) params["tags"] = filters.tags.join(",");

    return this.api.get("risks/count", params, false) as Observable<number>;
  }

  /**
   * Recover all the content (knowledge), questionnaire IDs and ServiceRequest templates
   * @param riskId
   * @param language
   * @returns The details of a risk
   */
  public getRisk(riskId: string, language: string): Observable<IRiskDetailsResponse> {
    return this.api.get(`risks/${encodeURIComponent(riskId)}`, { lang: language }, false) as Observable<IRiskDetailsResponse>;
  }

  /**
   * Returns the most popular tags or tags matching a search query
   * @param search Optional search text to filter tags
   * @params exclude Tags to exclude from serach since already selected
   * @returns List of tags
   */
  public getRiskTags(search?: string, exclude?: string[]): Observable<string[]> {
    const params: Record<string, string> = {};

    if (search?.trim()) params["search"] = search.trim();
    if (exclude?.length) params["exclude"] = exclude.join(",");

    return this.api.get("risks/tags", params, false) as Observable<string[]>;
  }
}
